/**
 * Helpers GED — préparation et affichage des pièces jointes.
 *
 * Contrat : 15 Mo max par fichier. Les photos sont compressées AVANT l'envoi
 * (JPEG, côté le plus long 1600 px, qualité 0.8).
 */

const MAX_TAILLE = 15 * 1024 * 1024; // 15 Mo
const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.8;

/** Vrai si le fichier est une image compressible côté navigateur. */
export function isCompressible(file: File): boolean {
    return /^image\/(jpeg|png|webp|bmp|gif)/i.test(file.type);
}

/** Lit un fichier en base64 nu (sans le préfixe data:). */
export function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
        reader.onerror = reject;
    });
}

/**
 * Compresse une photo → JPEG 1600 px / qualité 0.8 (contrat GED).
 * Renvoie { contenuBase64, mimeType, nomFichier, tailleOctets }.
 * Les autres fichiers sont lus tels quels (avec leur MIME d'origine).
 */
export async function prepareFichier(file: File): Promise<{
    contenuBase64: string;
    mimeType: string;
    nomFichier: string;
    tailleOctets: number;
}> {
    if (!isCompressible(file)) {
        if (file.size > MAX_TAILLE) {
            throw new Error('Fichier trop volumineux : 15 Mo maximum par pièce jointe.')
        }
        return {
            contenuBase64: await fileToBase64(file),
            mimeType: file.type || 'application/octet-stream',
            nomFichier: file.name,
            tailleOctets: file.size,
        }
    }

    // Photo → canvas → JPEG
    const bitmap = await createImageBitmap(file).catch(() => null)
    if (!bitmap) {
        // Décodage impossible → envoi brut si la taille passe
        if (file.size > MAX_TAILLE) {
            throw new Error('Fichier trop volumineux : 15 Mo maximum par pièce jointe.')
        }
        return {
            contenuBase64: await fileToBase64(file),
            mimeType: file.type,
            nomFichier: file.name,
            tailleOctets: file.size,
        }
    }

    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()

    const dataUrl = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
    const base64 = dataUrl.split(',')[1] || ''
    return {
        contenuBase64: base64,
        mimeType: 'image/jpeg',
        nomFichier: file.name.replace(/\.[^.]+$/, '') + '.jpg',
        tailleOctets: Math.round(base64.length * 3 / 4),
    }
}

/** Ouvre un contenu Base64 dans un nouvel onglet (visualisation / impression). */
export function ouvrirBase64(contenuBase64: string, mimeType = 'application/octet-stream') {
    const octets = Uint8Array.from(atob(contenuBase64), c => c.charCodeAt(0))
    const blob = new Blob([octets], { type: mimeType })
    window.open(URL.createObjectURL(blob), '_blank')
}

/** Télécharge un contenu Base64 sous son nom de fichier. */
export function telechargerBase64(contenuBase64: string, nomFichier: string, mimeType = 'application/octet-stream') {
    const octets = Uint8Array.from(atob(contenuBase64), c => c.charCodeAt(0))
    const blob = new Blob([octets], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = nomFichier || 'document'
    a.click()
    URL.revokeObjectURL(url)
}

/** Formate une taille en octets → « 184 Ko » / « 2,4 Mo ». */
export function formatTaille(octets?: number | null): string {
    if (octets == null || isNaN(octets)) return '—'
    if (octets < 1024) return `${octets} o`
    if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(0)} Ko`
    return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`
}
